import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { Point, PuckValue } from "../point/point.model";
import { PuckValueSocketClient } from "../socket/puck-value-socket-client";
import { useRequiredParams } from "../utils/url";
import { usePageVisibility } from "../utils/observe";
import { Alarm } from "../alarm/alarm.model";
import { PointTypeFactory } from "../point/point-type-factory";
import { parsePointName } from "../graphics/graphics.utils";

type RealtimePointUpdatesContextType = {
  subscribeToPoints: (points: (Point | Alarm)[]) => void;
  getPoint: (point: Point | Alarm) => Point | Alarm | undefined;
  updates: (Point | Alarm)[];
  closeConnection: () => void;
};

const RealtimePointUpdatesContext =
  createContext<RealtimePointUpdatesContextType | null>(null);

export function RealtimePointUpdatesProvider({
  children,
}: {
  children: ReactNode;
}) {
  // latest: Holds ALL subscribed points with their current merged values
  const [latest, setLatest] = useState<Map<string, Point | Alarm>>(new Map());

  // updates: Holds ONLY the points that changed in the last socket batch
  // This array gets replaced on each socket update with only the changed points
  const [updates, setUpdates] = useState<(Point | Alarm)[]>([]);
  const socketRef = useRef<PuckValueSocketClient | null>(null);
  const { companyId } = useRequiredParams({ companyId: "number" });
  const isPageVisible = usePageVisibility();

  /**
   * Creates a consistent key for both Point (from pointQuery) and PuckValue (from socket).
   * Both Point and PuckValue use the device's externalId for identification
   */
  const getPointKey = (point: Point | Alarm | PuckValue) => {
    // Remove limit extension from point name (limits use ":" separator, e.g., "pointName:0")
    // Control points use "#CONTROL#" separator (e.g., "pointName#CONTROL#210"), not ":",
    // so split(":")[0] returns the full control point name unchanged
    const rawPointName = "deviceId" in point ? point.pointName : point.name;
    const { pointBaseName } = parsePointName(rawPointName);

    return "deviceId" in point
      ? `${point.deviceId}-${pointBaseName}`
      : `${point.device.externalId}-${pointBaseName}`;
  };

  const getPoint = (point: Point | Alarm) => {
    const key = getPointKey(point);
    return latest.get(key);
  };

  const subscribeToPoints = useCallback(
    (pointsToSubscribe: (Point | Alarm)[]) => {
      // Store initial point values in latest (all subscribed points)
      pointsToSubscribe.forEach((point) => {
        const key = getPointKey(point);
        setLatest((prev) => {
          const next = new Map(prev);
          next.set(key, point);
          return next;
        });
      });
      // Create or reconnect socket with new points
      if (socketRef.current) {
        socketRef.current.close();
      }

      socketRef.current = new PuckValueSocketClient(
        companyId,
        pointsToSubscribe,
        (values: PuckValue[]) => {
          // Process socket batch: merge each PuckValue into Point
          // Store merged Points in both latest (all points) and updates (only changed ones)
          const batch: (Point | Alarm)[] = [];
          setLatest((prev) => {
            const next = new Map(prev);

            values.forEach((puckValue) => {
              const key = getPointKey(puckValue);
              const existingPoint = next.get(key);
              if (!existingPoint) {
                throw new Error(`Point not found`);
              }

              const updatedPoint =
                puckValue.value != undefined
                  ? {
                      ...existingPoint,
                      value: PointTypeFactory.value({
                        pointType: existingPoint.type,
                        value: puckValue,
                      }),
                    }
                  : existingPoint;

              next.set(key, updatedPoint);
              batch.push(updatedPoint);
            });

            return next;
          });

          setUpdates(batch);
        },
      );
    },
    [companyId],
  );

  // Disconnect when the tab becomes inactive in the users browser
  // Reconnect when the tab becomes active again
  useEffect(() => {
    if (isPageVisible) {
      socketRef.current?.connect();
    } else {
      socketRef.current?.disconnect();
    }
  }, [isPageVisible, subscribeToPoints]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, []);

  const closeConnection = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
  }, []);

  const value: RealtimePointUpdatesContextType = {
    subscribeToPoints,
    getPoint,
    updates,
    closeConnection,
  };

  return (
    <RealtimePointUpdatesContext.Provider value={value}>
      {children}
    </RealtimePointUpdatesContext.Provider>
  );
}

export const useRealtimePointUpdatesContext = () => {
  const ctx = useContext(RealtimePointUpdatesContext);
  if (!ctx) {
    throw new Error(
      "useRealtimePointUpdatesContext must be used inside RealtimePointUpdatesProvider",
    );
  }
  return ctx;
};
